import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { useAgTechHttpDeleteRequest } from "@agtech/core/data/http/AgTechApiDeleteRequests";
import { useAgTechHttpPostEntityRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { Payment } from "./InvoicePaymentData";
import { useAgTechHttpPutEntityRequest } from "@agtech/core/data/http/AgTechApiPutRequests";
import { InvoicePaymentDataGetInvoicePaymentsQuery } from "./InvoicePaymentDataQueries";
import { invalidateInvoiceQueries, InvoiceDataGetPaidInvoicesQuery } from "../InvoicingDataQueries";
import { useCurrentSale } from "../../sales/SaleDataStore";

export const usePaymentDataWritePaymentAction = (): AgTechDataAction<Payment, Payment> => {
    const currentSale = useCurrentSale();
    const salePaymentSubmitRequest = useAgTechHttpPostEntityRequest<Payment>();

    return {
        name: 'WritePayment',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity ? 'Updating payment...' : 'Creating payment...',
            actionConfirmationMessage: `Successfully ${props.originalEntity ? 'updated' : 'created' } payment`
        }),
        validate: async props  => {
            if (props.submittedEntity.amount <= 0) {
                props.validation.failWithWarning("Please specify a valid payment amount");
            }

            if (props.submittedEntity.invoiceId == 0) {
                props.validation.failWithWarning('An invoice was not specified. This is a bug.');
            }

            if (props.submittedEntity.payerId == 0) {
                props.validation.failWithWarning('Please specify a payer.');
            }
        },
        action: async props => {
            return await salePaymentSubmitRequest.post({
                path: `Invoice/${props.submittedEntity.invoiceId}/Payment`,
                postedData: props.submittedEntity,
            });
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, {
                saleId: currentSale.id,
                invoiceId: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoicePaymentDataGetInvoicePaymentsQuery,
                queryContext: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoiceDataGetPaidInvoicesQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    }
}

export const usePaymentDataEditPaymentAction = (): AgTechDataAction<Payment, Payment> => {
    const currentSale = useCurrentSale();
    const salePaymentSubmitRequest = useAgTechHttpPutEntityRequest<Payment>();

    return {
        name: 'EditPayment',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity ? 'Updating payment...' : 'Creating payment...',
            actionConfirmationMessage: `Successfully ${props.originalEntity ? 'updated' : 'created' } payment`
        }),
        validate: async props  => {
            if (props.submittedEntity.amount <= 0) {
                props.validation.failWithWarning("Please specify a valid payment amount");
            }

            if (props.submittedEntity.invoiceId == 0) {
                props.validation.failWithWarning('An invoice was not specified. This is a bug.');
            }

            if (props.submittedEntity.payerId == 0) {
                props.validation.failWithWarning('Please specify a payer.');
            }
        },
        action: async props => {
            return await salePaymentSubmitRequest.put({
                path: `Invoice/${props.submittedEntity.invoiceId}/Payment`,
                postedData: props.submittedEntity,
            });
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, {
                saleId: currentSale.id,
                invoiceId: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoicePaymentDataGetInvoicePaymentsQuery,
                queryContext: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoiceDataGetPaidInvoicesQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    }
}

export const usePaymentDataDeletePaymentAction = (): AgTechDataAction<Payment, boolean> => {
    const currentSale = useCurrentSale();
    const paymentDeleteRequest = useAgTechHttpDeleteRequest()

    return {
        name: 'DeletePayment',
        getConfiguration: props => ({
            actionExecutionMessage: 'Deleting payment',
            actionConfirmationMessage: 'Successfully deleted payment'
        }),
        confirm: async props => {
            return await props.appContext.confirmations.askForConfirmation({
                content: {
                    header: `Are you sure you want to delete the payment with amount "$${props.submittedEntity.amount}" from the invoice?`,
                    details: "The payment will be deleted and will no longer be applied to the invoice.",
                    width: '500px',
                    confirmationButtonText: 'Delete Payment'
                },
                submission: {
                    actionExecutionMessage: 'Deleting payment...',
                    actionConfirmationMessage: 'Payment deleted'
                }
            })
        },
        action: async props => {
            return await paymentDeleteRequest.delete({
                path: `Invoice/${props.submittedEntity.invoiceId}/Payment/${props.submittedEntity.id}`
            });
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, {
                saleId: currentSale.id,
                invoiceId: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoicePaymentDataGetInvoicePaymentsQuery,
                queryContext: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoiceDataGetPaidInvoicesQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    }
}